import { GlobalLogger } from "./debugLogger";

/* eslint-disable @typescript-eslint/no-var-requires */
const tf = require("@tensorflow/tfjs");
const { loadGraphModel } = require("@tensorflow/tfjs-converter");

declare global {
  interface Window {
    cardDetector?: CardDetector;
  }
}

class CardDetector {
  //model is fixed to IMG_SIZE x IMG_SIZE
  public static IMG_SIZE = 224;
  private model: any;
  private customModelPath = "";
  private cdnPath =
    "https://cardscan-models.s3-accelerate.amazonaws.com/card-detection-ft-class/model-v2-tfjs/model.json";
  private statusCallback: ((state: string) => void) | null = null;
  private _modelState: string;
  private laplaceFilter = tf
    .tensor2d([
      [0, 1, 0],
      [1, -4, 1],
      [0, 1, 0],
    ])
    .expandDims(-1)
    .expandDims(-1);

  public constructor(modelPath?: string) {
    GlobalLogger.earlyLog("Detector -- init");
    this._modelState = "loading";
    if (modelPath) this.customModelPath = modelPath;
    this.load_model().then((model) => {
      this.model = model;
      this.setModelState("loaded");
      this.warm_model();
    });
    //TODO: catch error loading model....
  }

  public setModelStatusCallback(callback: (state: string) => void) {
    this.statusCallback = callback;
  }

  private setModelState(state: string) {
    this._modelState = state;
    if (this.statusCallback) {
      this.statusCallback(state);
    }
  }

  get modelState(): string {
    return this._modelState;
  }

  private async load_model() {
    let model;
    const t0 = performance.now();
    try {
      model = await loadGraphModel("indexeddb://cardscan-class-model");
      GlobalLogger.earlyLog("Detector -- model loaded from cache");
    } catch {
      try {
        if (this.customModelPath) {
          model = await loadGraphModel(this.customModelPath);
          GlobalLogger.earlyLog("Detector -- model loaded from local file");
        }
        throw new Error("No custom model path provided");
      } catch (localFileError) {
        GlobalLogger.earlyLog(
          `Detector -- local file loaded failed: ${localFileError}`
        );
        try {
          model = await loadGraphModel(this.cdnPath);
          GlobalLogger.earlyLog("Detector -- model loaded from CDN");
        } catch (cdnError) {
          GlobalLogger.earlyLog(`Detector -- CDN load failed: ${cdnError}`);
          throw new Error(
            "Failed to load model from cache, local file, and CDN."
          );
        }
      }
    }
    const t1 = performance.now();
    GlobalLogger.earlyLog(`Detector -- model loaded in ${t1 - t0} ms`);
    return model;
  }

  private warm_model() {
    if (this.model == null) {
      GlobalLogger.error("Detector -- Unable to warm unloaded model");
      return;
    }

    //TOOD: check https, check IP isn't HOME.
    const expression =
      /((^\s*((([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5]))\s*$)|(^\s*((([0-9A-Fa-f]{1,4}:){7}([0-9A-Fa-f]{1,4}|:))|(([0-9A-Fa-f]{1,4}:){6}(:[0-9A-Fa-f]{1,4}|((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3})|:))|(([0-9A-Fa-f]{1,4}:){5}(((:[0-9A-Fa-f]{1,4}){1,2})|:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3})|:))|(([0-9A-Fa-f]{1,4}:){4}(((:[0-9A-Fa-f]{1,4}){1,3})|((:[0-9A-Fa-f]{1,4})?:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){3}(((:[0-9A-Fa-f]{1,4}){1,4})|((:[0-9A-Fa-f]{1,4}){0,2}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){2}(((:[0-9A-Fa-f]{1,4}){1,5})|((:[0-9A-Fa-f]{1,4}){0,3}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){1}(((:[0-9A-Fa-f]{1,4}){1,6})|((:[0-9A-Fa-f]{1,4}){0,4}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(:(((:[0-9A-Fa-f]{1,4}){1,7})|((:[0-9A-Fa-f]{1,4}){0,5}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:)))(%.+)?\s*$))/;

    //trying to stop other devs from wasting time.
    if (
      expression.test(window.location.hostname) &&
      window.location.hostname != "127.0.0.1"
    ) {
      GlobalLogger.earlyLog(
        "many browsers do not allow camera access on IP addresses. Please use localhost (web) or a tunnel like ngrox (mobile)"
      );

      //TODO: if dev/sandbox and on mobile and on ip... then show alert...
    }

    if (
      window.location.protocol != "https:" &&
      window.location.hostname != "localhost" &&
      window.location.hostname != "127.0.0.1"
    ) {
      GlobalLogger.earlyLog(
        "CardScanView: many browsers do not allow camera access on insecure connections. Please use localhost or https"
      );
    }

    tf.tidy(() => {
      const t0 = performance.now();
      const results = this.model.predict(
        tf
          .zeros([CardDetector.IMG_SIZE, CardDetector.IMG_SIZE, 3], "float32")
          .expandDims(0)
      );

      results.data().then(() => {
        this.setModelState("warmed");
        //save model on successful warm
        this.model.save("indexeddb://cardscan-class-model");
      });
      const t1 = performance.now();
      GlobalLogger.earlyLog(`Detector -- model warmed in ${t1 - t0} ms`);
      GlobalLogger.earlyLog(this.tf_mem_log(tf.memory()));
    });
  }
  private tf_mem_log(memory: any) {
    return `TF Memory Usage - t:${memory.numTensors}, gpu:${memory.numBytesInGPU}, gpufree:${memory.numBytesInGPUFree} buf:${memory.numDataBuffers}, mem:${memory.numBytes}`;
  }

  private prepare_results(modelResult: any, laplaceResult: any) {
    const response: any = {};

    const predictions = tf.sigmoid(modelResult);
    const score = predictions.dataSync()[0];
    response["modelClass"] = score < 0.5 ? "card" : "notcard";
    response["modelScore"] = score;
    response["modelProbability"] = score < 0.5 ? 1 - score : score;

    laplaceResult = laplaceResult.squeeze();
    laplaceResult = laplaceResult.div(tf.scalar(255)); //need to workaround #2475
    response["laplaceVariance"] = tf
      .moments(laplaceResult)
      .variance.dataSync()[0];
    response["laplaceMax"] = laplaceResult.max(1).dataSync()[0];

    //cleanup memory
    laplaceResult.dispose();
    modelResult.dispose();
    predictions.dispose();
    return response;
  }

  public async predict_image(
    canvas: HTMLCanvasElement,
    flipHorizontal: boolean,
    flipVertical: boolean
  ) {
    //on slow systems the model might not be loaded yet.
    if (this.model === undefined) {
      return null;
    }

    //TODO check canvas matches model image size

    //cleans up all tensors on exit
    return tf.tidy(() => {
      let t0 = performance.now();
      let data = tf.browser.fromPixels(canvas, 3);

      if (flipHorizontal || flipVertical) {
        const flipAxes = [];
        if (flipHorizontal) flipAxes.push(1);
        if (flipVertical) flipAxes.push(0);
        data = data.reverse(flipAxes);
      }

      let grey = data.mean(2);
      data = tf.cast(data, "float32");
      //normalize from 0 to 1
      data = data.div(tf.scalar(255));
      GlobalLogger.debug(
        `image-prepare: ${performance.now() - t0} ms`,
        "detector"
      );

      t0 = performance.now();
      const result = this.model.predict(data.expandDims(0));
      GlobalLogger.debug(
        `model-predict: ${performance.now() - t0} ms`,
        "detector"
      );
      grey = grey.expandDims(0).expandDims(-1);
      t0 = performance.now();
      const lapResult = grey.conv2d(this.laplaceFilter, 1, "valid");
      GlobalLogger.debug(`laplacian: ${performance.now() - t0} ms`, "detector");

      GlobalLogger.debug(this.tf_mem_log(tf.memory()), "detector");
      return this.prepare_results(result, lapResult);
    });
  }
}

class DetectorWrapper {
  public static image_size: number = CardDetector.IMG_SIZE;

  static get(): CardDetector {
    if (window.cardDetector === undefined) {
      window.cardDetector = new CardDetector();
    }
    return window.cardDetector;
  }

  static init(modelPath?: string) {
    if (window.cardDetector === undefined) {
      window.cardDetector = new CardDetector(modelPath);
    }
  }

  static cleanUp() {
    delete window.cardDetector;
  }
}

export class CardScanModel {
  static warm(modelPath?: string) {
    GlobalLogger.earlyLog("Detector -- warm model");
    DetectorWrapper.init(modelPath);
  }
}

export default DetectorWrapper;
